// Green Screen Yapper Ad's images stage (live's `images-stage`, `card-check-line`,
// `yapper-still-card` and `script-summary`; spec G14-green-screen-yapper, PR h): approval 2's five
// stills, the card read back, Back to script and the approved script's summary.
import { Button, Group, Stack, Text } from "@mantine/core";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";

import type {
  GreenScreenCardCheck,
  GreenScreenDraftStill,
  GreenScreenPlan,
  GreenScreenScript,
  GreenScreenStillName,
} from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { FieldLabel } from "../../../ui/FieldLabel";
import { CardPreview } from "./GreenScreenScript";
import { StillCard } from "./StillCard";
import { STILL_LABELS, STILL_ORDER, stillWaiting } from "./green-screen-draft";
import podcast from "./podcast.module.css";
import classes from "./green-screen.module.css";

/** Live's words (`library-view/constants.ts`, `messages.ts`). */
const IMAGES_TOOLTIP =
  "Approval 2: the creator still, the three poses each clip opens on, and the card read back word " +
  "for word. Make any of them again before the video.";
const BACK_TO_SCRIPT = "Back to script";
const BACK_TO_SCRIPT_CONFIRM =
  "Edit the script again? Every image (the creator, the poses and the card) will be made again after " +
  "you approve it.";
const READING_CARD = "Reading the card back";
const CARD_READS_EXACTLY = "The card reads exactly as approved";
const PENDING = { status: "pending", url: null, problem: null } as const;

/** "Clips 5 · 8 · 6 · 10 s + card clip 5 s" (live's `toClipLengthsLabel`). */
export function clipLengths(plan: GreenScreenPlan): string {
  return `Clips ${plan.clips.map((clip) => clip.duration_s).join(" · ")} s + card clip ${plan.card_clip_s} s`;
}

/** Live's card check line: reading, the card read exactly ("card 2 of 2" after a redraw), or
 * each problem as a warning. */
export function cardCheckLines(check: GreenScreenCardCheck | null, checking: boolean): string[] {
  if (checking) return [READING_CARD];
  if (check === null) return [];
  if (check.problems.length === 0) {
    const count = check.card_count;
    return [count > 1 ? `✓ ${CARD_READS_EXACTLY} (card ${count} of ${count})` : `✓ ${CARD_READS_EXACTLY}`];
  }
  return check.problems.map((problem) => `⚠ card: ${problem}`);
}

function stillItem(still: GreenScreenDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface GreenScreenImagesProps {
  script: GreenScreenScript;
  plan: GreenScreenPlan | null;
  stills: readonly GreenScreenDraftStill[];
  cardCheck: GreenScreenCardCheck | null;
  checking: boolean;
  busy: boolean;
  resetting: boolean;
  /** Live's refused-clip notice on each still a refused clip of the last run started on. */
  flags: Readonly<Partial<Record<GreenScreenStillName, string>>>;
  /** The last run's video model refused a talking clip: Back opens Revise, asked nothing. */
  canRevise: boolean;
  onRegenerate: (name: GreenScreenStillName) => void;
  onBack: () => void;
  onRevise: () => void;
  onStartOver: () => void;
}

/**
 * Live's images stage: "Images" with its tooltip, the clips' lengths and "Back to script" (asked
 * first: every image is made again after the next approval); the card check line; the five
 * stills, each with live's caption, ↻ and a preview; the approved script's summary. After a run
 * whose video model refused a talking clip, Back opens Revise instead, at once (live's
 * `reviseClips`), and each refused clip's still carries live's notice. Ours also offers "Start
 * over", as every staged draft's.
 */
export function GreenScreenImages(props: GreenScreenImagesProps) {
  const { script, plan, stills, cardCheck, checking, busy, resetting, flags, canRevise } = props;
  const { onRegenerate, onBack, onRevise, onStartOver } = props;
  const [preview, setPreview] = useState<GreenScreenStillName | null>(null);
  const [confirm, setConfirm] = useState<"back" | "over" | null>(null);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Images" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text component="div" className={podcast.castTitle}>
          <FieldLabel label="Images" info={IMAGES_TOOLTIP} />
        </Text>
        <Group gap={8} wrap="nowrap">
          {plan === null ? null : <Text className={podcast.meta}>{clipLengths(plan)}</Text>}
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<ArrowLeft size={14} aria-hidden />}
            disabled={busy}
            onClick={() => (canRevise ? onRevise() : setConfirm("back"))}
          >
            {BACK_TO_SCRIPT}
          </Button>
          <Button variant="subtle" size="compact-sm" disabled={resetting || busy} onClick={() => setConfirm("over")}>
            Start over
          </Button>
        </Group>
      </Group>
      {cardCheckLines(cardCheck, checking).map((line) => (
        <Text key={line} className={line.startsWith("⚠") ? podcast.warning : podcast.meta}>
          {line}
        </Text>
      ))}
      <div className={podcast.stills}>
        {STILL_ORDER.map((name) => (
          <StillCard
            key={name}
            label={STILL_LABELS[name]}
            still={stills.find((each) => each.name === name) ?? PENDING}
            waiting={stillWaiting(name, stills)}
            busy={busy}
            onRegenerate={() => onRegenerate(name)}
            onFixLabel={null}
            onPreview={() => setPreview(name)}
            flag={flags[name] ?? null}
          />
        ))}
      </div>
      <details className={classes.summary}>
        <summary>Script</summary>
        <Stack gap={8}>
          <div>
            {script.lines.map((line) => (
              <Text key={line.id} className={classes.cardLine}>
                <span className={classes.marker}>{`${line.clip}·${line.id}`}</span> {line.text}
              </Text>
            ))}
          </div>
          <div>
            <Text className={classes.label}>Card</Text>
            <CardPreview script={script} />
          </div>
          <div>
            <Text className={classes.label}>Creator</Text>
            <Text className={classes.cardLine}>
              {`${script.creator.look} (${script.creator.gender}, ${script.creator.age})`}
            </Text>
          </div>
          <div>
            <Text className={classes.label}>Beat</Text>
            {script.beats.map((beat) => (
              <Text key={beat.clip} className={classes.cardLine}>{`${beat.clip}: ${beat.action}`}</Text>
            ))}
          </div>
        </Stack>
      </details>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as GreenScreenStillName | null)}
      />
      <ConfirmModal
        opened={confirm === "back"}
        title={BACK_TO_SCRIPT}
        confirmLabel={BACK_TO_SCRIPT}
        cancelLabel="Cancel"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          onBack();
          setConfirm(null);
        }}
      >
        <Text size="sm">{BACK_TO_SCRIPT_CONFIRM}</Text>
      </ConfirmModal>
      <ConfirmModal
        opened={confirm === "over"}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          onStartOver();
          setConfirm(null);
        }}
      >
        <Text size="sm">The script and the five images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </Stack>
  );
}
